import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize a single string value
 *
 * Removes ALL HTML tags and attributes to prevent XSS attacks.
 * Examples:
 *   "<script>alert('xss')</script>" → ""
 *   "<b>Hello</b> World" → "Hello World"
 *   "Normal text" → "Normal text"
 *
 * @param input - Raw string input from user
 * @returns Sanitized string with HTML stripped
 */
export function sanitizeString(input: string): string {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],      // Strip ALL HTML tags
    ALLOWED_ATTR: [],      // Strip ALL attributes
    KEEP_CONTENT: true     // Keep text content, remove only tags
  });
}

/**
 * Sanitize all string values in a form data object
 *
 * Recursively processes all fields in the form submission,
 * sanitizing any string values while preserving numbers, booleans, etc.
 *
 * @param data - Raw form data object
 * @returns Sanitized form data object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sanitizeFormData(data: Record<string, any>): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      // Sanitize string values
      sanitized[key] = sanitizeString(value);
    } else if (value === null || value === undefined) {
      // Preserve null/undefined
      sanitized[key] = value;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeFormData(value);
    } else {
      // Preserve numbers, booleans, arrays as-is
      sanitized[key] = value;
    }
  }

  return sanitized;
}
